import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Mail, Sparkles, Heart, Zap, ShieldCheck } from "lucide-react";
import { ProductHeader, ProductFooter } from "@/pages/Products";
import { CRAVY_CONFIG } from "@/config";

const LOGO = "/assets/cravy-logo.png";

const VALUES = [
  { icon: Heart,       t: "People first.",   d: "Every Cravy install starts with the people using it — residents, employees, guests. If it doesn't make their day a little better, it doesn't ship." },
  { icon: Zap,         t: "Operate beautifully.", d: "Stocking on time. Refunds answered same day. Machines spotless. The little stuff is the whole thing." },
  { icon: ShieldCheck, t: "Show up, every day.",  d: "Our properties never have to chase us. We respond same-day, every day — and we mean it." },
];

export default function About() {
  useEffect(() => {
    document.title = "About Cravy — The team behind your market";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-body bg-cravy-cream min-h-screen">
      <ProductHeader />

      {/* Hero */}
      <section className="bg-cravy-ink cravy-grain pt-[72px] relative overflow-hidden">
        <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-cravy-orange/15 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-28">
          <Link to="/" className="text-cravy-cream/60 hover:text-cravy-orange text-[13px] inline-flex items-center gap-2" data-testid="about-back">← Back to home</Link>
          <div className="mt-6 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cravy-orange/10 border border-cravy-orange/25 text-cravy-orange text-[12px] font-semibold tracking-wide uppercase">
                <Sparkles className="w-3.5 h-3.5" /> Our story
              </div>
              <h1 className="mt-6 font-display font-extrabold text-cravy-cream tracking-[-0.03em] leading-[0.95] text-[44px] sm:text-[60px] lg:text-[74px]">
                The team behind <span className="italic text-cravy-orange">your market.</span>
              </h1>
              <p className="mt-6 text-cravy-cream/75 text-[17px] lg:text-[19px] max-w-xl leading-relaxed">
                Cravy was started by Ted Kemp and Jayson Love — two operators looking to shake up the vending machine industry. We got tired of broken, neglected machines that nobody wanted to use. We thought properties — and the people inside them — deserved better.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-[28px] bg-cravy-ink border border-cravy-orange/30 p-8 shadow-2xl flex items-center justify-center aspect-square">
                <img src={LOGO} alt="Cravy" className="w-full h-full object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The story */}
      <section className="bg-cravy-cream py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em]">Why Cravy exists</div>
          <h2 className="mt-3 font-display font-extrabold text-[36px] sm:text-[48px] leading-[1.05] tracking-[-0.03em] mb-8">
            We thought there had to be a better way.
          </h2>
          <div className="space-y-5 text-cravy-ink/80 text-[16px] lg:text-[17px] leading-[1.75]">
            <p>
              Every property has a few of them: the dusty vending machine in the corner that takes your dollar and gives you nothing back. The one with sticky buttons and chip bags from 2019. The one where the support number rings forever.
            </p>
            <p>
              We knew there was a better way to do this — a smart-market amenity that actually feels modern, gets stocked on time, takes every payment method, and never makes the property manager pick up the phone for a refund.
            </p>
            <p>
              We started Cravy with a simple promise: zero cost to the property, zero liability, and an amenity that just works. We invest in the machine, the inventory, and the operations. The property hosts the space. The community gets a 24/7 market they actually want to use.
            </p>
            <p>
              We're local, and we actually care. We answer our phones. We restock when we say we will. We treat every property like it's the only one on our route — because that's how our people-first business runs. And if it's ever not working for you, we cancel without a fight.
            </p>
            <p className="italic font-display text-cravy-orangeDeep text-[20px] lg:text-[22px] pt-4 border-t border-cravy-ink/8">
              — Jayson Love &amp; Ted Kemp, Co-founders, Cravy / TK Love LLC
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-cravy-ink cravy-grain py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] rounded-full bg-cravy-orange/8 blur-[140px]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-2xl">
            <div className="text-cravy-orange font-semibold text-[12px] uppercase tracking-[0.25em]">What we believe</div>
            <h2 className="mt-3 font-display font-extrabold text-cravy-cream text-[40px] sm:text-[54px] leading-[0.95] tracking-[-0.03em]">
              Three things, on repeat.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mt-12">
            {VALUES.map((v, i) => (
              <div key={i} className="rounded-3xl border border-white/8 bg-white/[0.03] p-7 hover:bg-white/[0.06] hover:border-cravy-orange/30 transition-all">
                <div className="w-11 h-11 rounded-2xl bg-cravy-orange/15 text-cravy-orange flex items-center justify-center">
                  <v.icon className="w-5 h-5" />
                </div>
                <div className="mt-5 font-display font-bold text-cravy-cream text-[22px] tracking-tight">{v.t}</div>
                <div className="mt-2 text-cravy-cream/65 text-[14px] leading-relaxed">{v.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="bg-cravy-cream2 py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em]">The founders</div>
          <h2 className="mt-3 font-display font-extrabold text-[40px] sm:text-[54px] leading-[0.95] tracking-[-0.03em] mb-12">
            Two operators. <span className="italic text-cravy-orangeDeep">One promise.</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { name: "Jayson Love", role: "Co-founder & Co-owner", phone: CRAVY_CONFIG.phones.jayson, email: CRAVY_CONFIG.emails.jayson,
                bio: "Jayson runs partnerships and operations. If you've ever talked to Cravy, you've probably talked to Jayson." },
              { name: "Ted Kemp",    role: "Co-founder & Co-owner", phone: CRAVY_CONFIG.phones.ted, email: CRAVY_CONFIG.emails.ted,
                bio: "Ted runs installs and service. He's the reason every restock happens on time and every machine looks new on Monday." },
            ].map((p, i) => (
              <div key={i} className="bg-white rounded-3xl p-7 border border-cravy-ink/5">
                <div className="w-16 h-16 rounded-2xl bg-cravy-orange/15 text-cravy-orange flex items-center justify-center font-display font-extrabold text-[24px] tracking-tight">
                  {p.name.split(" ").map(n => n[0]).join("")}
                </div>
                <div className="mt-5 font-display font-extrabold text-[26px] tracking-tight">{p.name}</div>
                <div className="text-cravy-orangeDeep text-[12px] font-semibold uppercase tracking-[0.15em] mt-1">{p.role}</div>
                <p className="mt-4 text-cravy-ink/70 text-[14px] leading-relaxed">{p.bio}</p>
                <div className="mt-5 space-y-2">
                  <a href={`tel:${p.phone.replace(/[^\d+]/g, "")}`} className="flex items-center gap-2 text-cravy-orangeDeep font-semibold text-[14px] hover:gap-3 transition-all">
                    <Phone className="w-4 h-4" /> {p.phone}
                  </a>
                  <a href={`mailto:${p.email}`} className="flex items-center gap-2 text-cravy-orangeDeep font-semibold text-[14px] hover:gap-3 transition-all">
                    <Mail className="w-4 h-4" /> {p.email}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cravy-ink cravy-grain py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-cravy-orange/12 blur-[140px]" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <h2 className="font-display font-extrabold text-cravy-cream text-[36px] sm:text-[48px] lg:text-[56px] leading-[0.95] tracking-[-0.03em]">
            Want a Cravy market at your property?
          </h2>
          <p className="mt-5 text-cravy-cream/70 text-[16px] max-w-xl mx-auto">
            Tell us about your space — we'll respond within one business day with next steps and a 10-minute walkthrough.
          </p>
          <Link to="/#partner" className="btn-orange mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full text-[15px]" data-testid="about-cta-partner">
            Partner with Cravy <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <ProductFooter />
    </div>
  );
}
